"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import { api } from "@/lib/api";
import { getPendingEval, setPendingEval, clearPendingEval, PendingEval } from "@/lib/pendingEval";

// Ending a session drops the student straight back on the home page, so THIS is
// what tells them what happened to their supervisor report. Two beats, both as a
// top-right toast:
//   1. "running"  — the supervisor is writing the report (shown the moment they land)
//   2. "ready"    — it's done, with a button that opens the report
// plus two terminal-but-quiet cases: "failed" (retry) and "too_short" (nothing to
// supervise). Dismissing the running toast only closes THAT beat — the ready toast
// still arrives, which is the whole point of leaving the session screen.
//
// Deliberately no global state library: a self-contained poller + local state,
// mounted by AppShell so it follows the student across every page of the app.

const POLL_INTERVAL_MS = 5000;

type Kind = "running" | "ready" | "failed" | "too_short";

// How long each beat stays on screen before closing itself, with a draining bar
// along the bottom edge so the countdown is visible rather than a surprise.
// "ready" gets the longest run because it is the one beat with something to click;
// the others are pure information and can leave sooner. Hovering (or focusing)
// the toast pauses the countdown, so nothing disappears while it is being read.
// null = no countdown at all: "failed" is the student's only handle on a report
// that did not happen, so it waits for them — retry or close, in their own time.
const AUTO_DISMISS_MS: Record<Kind, number | null> = {
  running: 8000,
  ready: 12000,
  failed: null,
  too_short: 10000,
};
const TICK_MS = 100;
// Enter/leave transition. Short on purpose: the toast has to feel like it settled
// into place, not like it is putting on a show while the student is reading.
const ANIM_MS = 180;

export function PendingEvalWatcher() {
  const t = useTranslations("reportToast");
  const locale = useLocale();
  const router = useRouter();
  const [entry, setEntry] = useState<PendingEval | null>(null);
  const [kind, setKind] = useState<Kind | null>(null);
  // Dismissal is tracked PER BEAT: closing "hazırlanıyor" must not swallow "hazır".
  const [dismissed, setDismissed] = useState<Kind | null>(null);
  // Milliseconds left before this beat closes itself (drives the bottom bar).
  const [remaining, setRemaining] = useState<number | null>(null);
  // Transition state: `shown` flips on one frame after mount (so the browser has a
  // "from" style to animate out of), `leaving` holds the card on screen for the
  // exit transition before the beat is actually torn down.
  const [shown, setShown] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const pausedRef = useRef(false);
  const closeRef = useRef<(() => void) | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A student who asked the OS for less motion gets the toast with no movement at
  // all — it appears and disappears instantly, everything else stays identical.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const stored = getPendingEval();
    if (!stored) return; // nothing to watch — the poller never starts
    // Non-null alias: TypeScript does not carry the guard's narrowing into the
    // nested poller, and this keeps the whole effect free of null assertions.
    const pending: PendingEval = stored;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Optimistic first beat, so landing on the home page says something straight
    // away instead of staying blank until the first round-trip returns. An entry
    // already marked ready re-opens on that beat (no "hazırlanıyor" flash).
    setEntry(pending);
    setKind(pending.ready ? "ready" : "running");
    if (pending.ready) return () => { cancelled = true; };

    async function check() {
      try {
        const { status } = await api.getEvaluationStatus(pending.sessionId);
        if (cancelled) return;
        if (status === "ready") {
          // Kept in localStorage on purpose: the report link is worth holding onto
          // until the student acknowledges it (opening the report clears it too).
          setPendingEval({ ...pending, ready: true });
          setKind("ready");
          setDismissed(null);
          return;
        }
        if (status === "failed") {
          clearPendingEval(pending.sessionId);
          setKind("failed");
          setDismissed(null);
          return;
        }
        if (status === "too_short") {
          // No report is coming and retrying can't change that — say it once.
          clearPendingEval(pending.sessionId);
          setKind("too_short");
          setDismissed(null);
          return;
        }
        if (status === "none") {
          // Job lost (server restart mid-run) — re-trigger it and keep waiting.
          await api.evaluateSession(pending.sessionId, locale);
        }
      } catch {
        // Session gone / auth changed — the entry is stale, stop tracking it.
        clearPendingEval();
        setKind(null);
        return;
      }
      timer = setTimeout(check, POLL_INTERVAL_MS);
    }

    // First check almost immediately: the job was kicked off from the session
    // screen a moment ago, so the answer is usually one short round-trip away.
    timer = setTimeout(check, 800);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [locale]);

  const visible = !!entry && !!kind && dismissed !== kind;

  // Closing a beat: "hazırlanıyor" only hides itself (the pending entry stays so
  // the "hazır" beat can still fire); any other beat is terminal and ends the run.
  const finishClose = useCallback(() => {
    if (kind === "running") { setDismissed("running"); return; }
    clearPendingEval();
    setKind(null);
  }, [kind]);

  // Shared by the close button and the countdown, so both behave identically: play
  // the exit transition first, tear the beat down only once it has finished.
  const close = useCallback(() => {
    if (reduceMotion) { finishClose(); return; }
    if (leaveTimer.current) return; // already on its way out
    setLeaving(true);
    leaveTimer.current = setTimeout(() => {
      leaveTimer.current = null;
      setLeaving(false);
      finishClose();
    }, ANIM_MS);
  }, [finishClose, reduceMotion]);
  useEffect(() => () => { if (leaveTimer.current) clearTimeout(leaveTimer.current); }, []);
  // Held in a ref so the countdown interval always calls the current version
  // without having to restart every time `close` is re-created.
  useEffect(() => { closeRef.current = close; }, [close]);

  // Enter transition, re-run per beat: a beat change (running → ready) swaps the
  // content of the same card, so it is re-introduced rather than silently rewritten.
  useEffect(() => {
    if (!visible) { setShown(false); return; }
    // A beat that arrives while the previous one is fading out cancels that exit —
    // otherwise the pending timer would tear down the beat that just replaced it.
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    setShown(false);
    setLeaving(false);
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [visible, kind]);

  // Countdown to the automatic close. Restarts whenever a new beat arrives, and
  // holds while the pointer or keyboard focus is on the toast so nothing vanishes
  // mid-read (or mid-click).
  useEffect(() => {
    if (!visible || !kind) { setRemaining(null); return; }
    const total = AUTO_DISMISS_MS[kind];
    if (total === null) { setRemaining(null); return; } // this beat waits for the user
    setRemaining(total);
    let left = total;
    const id = setInterval(() => {
      if (pausedRef.current) return;
      left -= TICK_MS;
      if (left <= 0) {
        clearInterval(id);
        setRemaining(0);
        closeRef.current?.();
        return;
      }
      setRemaining(left);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [visible, kind]);

  if (!visible || !entry || !kind) return null;

  const reportHref = (() => {
    const params = new URLSearchParams();
    if (entry.caseId) params.set("case", entry.caseId);
    if (entry.patient) params.set("patient", entry.patient);
    const qs = params.toString();
    return `/report/${entry.sessionId}${qs ? `?${qs}` : ""}`;
  })();

  function openReport() {
    clearPendingEval();
    setKind(null);
    router.push(reportHref);
  }

  // Message keys per beat: <prefix>Title / <prefix>Body / <prefix>BodyWith.
  const PREFIX: Record<Kind, string> = {
    running: "running", ready: "ready", failed: "failed", too_short: "tooShort",
  };
  const key = PREFIX[kind];
  const title = t(`${key}Title`);
  const body = entry.patient ? t(`${key}BodyWith`, { name: entry.patient }) : t(`${key}Body`);
  // Only the two actionable beats carry a button.
  const cta = kind === "ready" ? t("readyCta") : kind === "failed" ? t("failedCta") : null;

  // Ready is the only celebratory beat; running/too_short are neutral, failed warns.
  const iconStyle = kind === "failed"
    ? { background: "var(--accent-warm-pale)", color: "var(--accent-warm)" }
    : { background: "var(--accent-light)", color: "var(--accent)" };

  // On screen and staying there: mounted, past the first frame, not on its way out.
  const settled = shown && !leaving;

  const total = AUTO_DISMISS_MS[kind];
  const left = total === null ? 0 : remaining ?? total;
  // Seconds shown to the user: round UP so a fresh toast reads "8 sn", not "7 sn",
  // and the number never sits on 0 while the card is still there.
  const secondsLeft = Math.max(1, Math.ceil(left / 1000));

  return (
    <div
      role="status"
      aria-live="polite"
      // overflow-hidden + no padding on the card itself: the countdown bar runs
      // edge to edge along the bottom, inside the rounded corners.
      className="fixed top-5 right-5 z-50 w-[min(22rem,calc(100vw-2.5rem))] overflow-hidden rounded-2xl border shadow-lg"
      style={{
        background: "var(--surface)",
        borderColor: "var(--border)",
        // `settled` is the resting state; both the pre-enter and the leaving state
        // are the same faded, slightly smaller card, so it grows in and shrinks out.
        ...(reduceMotion
          ? {}
          : {
              opacity: settled ? 1 : 0,
              transform: settled ? "scale(1)" : "scale(0.96)",
              transformOrigin: "top right",
              transition: `opacity ${ANIM_MS}ms ease-out, transform ${ANIM_MS}ms ease-out`,
            }),
      }}
      // The countdown holds while the toast is being read or reached for.
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onFocusCapture={() => { pausedRef.current = true; }}
      onBlurCapture={() => { pausedRef.current = false; }}
    >
      <div className="flex items-start gap-3 p-4">
        <span
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
          style={iconStyle}
        >
          {kind === "running" ? (
            // Spinning ring: the one beat that is still in motion.
            <span
              aria-hidden
              className="h-4 w-4 animate-spin rounded-full border-2 border-current"
              style={{ borderTopColor: "transparent" }}
            />
          ) : kind === "ready" ? (
            <svg aria-hidden className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 8.25h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
          ) : kind === "too_short" ? (
            <svg aria-hidden className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm3.75 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm3.75 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
            </svg>
          ) : (
            <svg aria-hidden className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
            </svg>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
          <p className="mt-0.5 text-xs text-[var(--text-secondary)]">{body}</p>
          {cta && (
            <button
              onClick={openReport}
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold text-white hover:opacity-90"
              style={{ background: "var(--accent)" }}
            >
              {cta}
              <svg aria-hidden className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </button>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <button
            onClick={close}
            aria-label={t("dismiss")}
            className="rounded-full p-1 text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
          >
            <svg aria-hidden className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
          {/* The number spells out what the bar below only implies. */}
          {total !== null && (
            <span className="text-[10px] font-medium tabular-nums text-[var(--text-muted)]">
              {t("autoClose", { seconds: secondsLeft })}
            </span>
          )}
        </div>
      </div>

      {/* Countdown bar, flush with the card's bottom edge. Absent on the beats
          that never close by themselves. */}
      {total !== null && (
        <div className="h-[3px] w-full" style={{ background: "var(--border)" }}>
          <div
            className="h-full"
            style={{
              width: `${Math.max(0, (left / total) * 100)}%`,
              background: "var(--accent)",
              // Matches the tick so the bar glides instead of stepping; `linear`
              // keeps the drain rate honest.
              transition: `width ${TICK_MS}ms linear`,
            }}
          />
        </div>
      )}
    </div>
  );
}
